import React, { startTransition, useEffect, useRef, useState } from 'react';
import { callAPIAsync, callAPISync, getMetricsFilterOptions, getOperationMetrics, getUploadMetrics } from '../../../../utils/api';
import { MetricsFilterMappings, MetricsFilterOptions, MetricsFilters } from '../../../../types/admin';
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Fab,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Menu,
	MenuItem,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import LabelledSelect from '../../../LabelledSelect';
import { ArrowDropDown, Delete, Download, KeyboardDoubleArrowUp, Search } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import { recordsToCsv, sortArr } from '../../../../utils/string';
import MetricsTable from './MetricsTable';
import { SnackbarAlert } from '../../../SnackbarAlert';
import { useLogto } from '@logto/react';

dayjs.extend(utc);

const MetricsViewTab: React.FC = () => {
	const logto = useLogto();

	const [filters, setFilters] = useState<MetricsFilters>({});
	const [currentFilters, setCurrentFilters] = useState<MetricsFilterOptions[]>([]);
	const [possibleFilters, setPossibleFilters] = useState<MetricsFilterOptions[]>(
		sortArr(Object.keys(MetricsFilterMappings)) as MetricsFilterOptions[]
	);
	// this may need to be fetched after each update if it proves to be a significantly large list
	const [autocompleteOptions, setAutocompleteOptions] = useState<Record<string, string[]>>({});
	const [requestedMetrics, setRequestedMetrics] = useState<string>('');
	const [filterAnchor, setFilterAnchor] = useState<HTMLButtonElement | null>();
	const [previewLoaded, setPreviewLoaded] = useState<boolean>(false);
	const [loadError, setLoadError] = useState<string>('');
	const [loadedData, setLoadedData] = useState<Record<string, string>[]>([]);
	const [showFab, setShowFab] = useState<boolean>(false);

	const tabHolder = useRef<HTMLDivElement | null>(null);

	const loadingData = previewLoaded && loadedData.length === 0;

	window.addEventListener('scroll', () => {
		if (window.scrollY > 0) {
			setShowFab(true);
		} else {
			setShowFab(false);
		}
	});

	const getFilterOptions = () => {
		startTransition(() => {
			callAPISync(logto, (token) => {
				getMetricsFilterOptions(token).then(async (res) => {
					setAutocompleteOptions(await res.json());
				});
			});
		});
	};

	const resetLoaded = () => {
		if (previewLoaded) {
			setPreviewLoaded(false);
			setLoadError('');
		}
	};

	const submitRequest = async (): Promise<Record<string, string>[]> => {
		let metrics: Record<string, string>[] = [];
		if (requestedMetrics === 'upload') {
			const metricsResp = await callAPIAsync(logto, (token) => getUploadMetrics(token, filters));
			if (metricsResp) {
				if (metricsResp.status === 204) {
					setLoadError(`Request for upload metrics returned no results, check your filters`);
				} else if (metricsResp.ok) {
					metrics = await metricsResp.json();
				} else {
					setLoadError(`Failed to fetch upload metrics - ${metricsResp.status}: ${metricsResp.statusText}`);
				}
			}
		} else if (requestedMetrics === 'operation') {
			const metricsResp = await callAPIAsync(logto, (token) => getOperationMetrics(token, filters));
			if (metricsResp) {
				if (metricsResp.status === 204) {
					setLoadError(`Request for operation metrics returned no results, check your filters`);
				} else if (metricsResp.ok) {
					metrics = await metricsResp.json();
				} else {
					setLoadError(`Failed to fetch operation metrics - ${metricsResp.status}: ${metricsResp.statusText}`);
				}
			}
		}

		if (metrics.length === 0) {
			setPreviewLoaded(false);
		}
		return metrics;
	};

	const handleChangeFilter = (filterName: MetricsFilterOptions, newValue: string | number | boolean) => {
		resetLoaded();
		setFilters({
			...filters,
			[filterName]: newValue,
		});
	};

	const handleAddFilter = (filterName: MetricsFilterOptions) => {
		resetLoaded();
		setCurrentFilters([...currentFilters, filterName]);
		const leftoverFilters = sortArr(possibleFilters.filter((val) => val !== filterName)) as MetricsFilterOptions[];
		setPossibleFilters(leftoverFilters);
		if (leftoverFilters.length === 0) {
			setFilterAnchor(null);
		}

		if (MetricsFilterMappings[filterName].componentType === 'date') {
			handleChangeFilter(filterName, dayjs.utc().startOf('day').valueOf());
		} else if (MetricsFilterMappings[filterName].componentType === 'autocomplete') {
			handleChangeFilter(filterName, '');
		}
	};

	const handleRemoveFilter = (filterName: MetricsFilterOptions) => {
		resetLoaded();
		setPossibleFilters([...possibleFilters, filterName]);
		const leftoverFilters = sortArr(currentFilters.filter((val) => val !== filterName)) as MetricsFilterOptions[];
		setCurrentFilters(leftoverFilters);
		setFilters({
			...filters,
			[filterName]: undefined,
		});
	};

	const downloadData = async () => {
		const metrics: Record<string, string>[] = loadedData.length > 0 ? loadedData : await submitRequest();

		if (Object.keys(metrics).length > 0) {
			const csv = recordsToCsv(metrics, Object.keys(metrics[0]));

			const csvBlob = new Blob([csv], { type: 'text/csv' });

			const downloadLink = document.createElement('a');
			downloadLink.href = window.URL.createObjectURL(csvBlob);
			downloadLink.setAttribute('download', `${requestedMetrics}-metrics-${dayjs().format('YYYY-MM-DD')}.csv`);

			document.body.appendChild(downloadLink);
			downloadLink.click();

			if (downloadLink.parentNode) {
				downloadLink.parentNode.removeChild(downloadLink);
			}
		}
	};

	const getFilterComponent = (filterName: MetricsFilterOptions) => {
		switch (MetricsFilterMappings[filterName].componentType) {
			case 'string':
				return (
					<TextField
						type="text"
						fullWidth
						variant="outlined"
						value={filters[filterName] || ''}
						onChange={(e) => handleChangeFilter(filterName, e.target.value)}
						slotProps={{ htmlInput: { 'aria-label': `Enter Value for ${MetricsFilterMappings[filterName].name} filter` } }}
						data-testid={`${filterName}-entry-field`}
					/>
				);
			case 'autocomplete':
				return (
					<Autocomplete
						value={filters[filterName]}
						onChange={(_, newVal) => {
							handleChangeFilter(filterName, newVal || '');
						}}
						options={autocompleteOptions[filterName] || []}
						fullWidth
						freeSolo
						renderInput={(params) => (
							<TextField
								{...params}
								slotProps={{
									htmlInput: { ...params.inputProps, 'aria-label': `Enter Value for ${MetricsFilterMappings[filterName].name} filter` },
								}}
							/>
						)}
						data-testid={`${filterName}-entry-field`}
					/>
				);
			case 'number':
				return (
					<TextField
						type="text"
						inputMode="numeric"
						fullWidth
						slotProps={{
							htmlInput: { pattern: '[0-9]*', 'aria-label': `Enter Value for ${MetricsFilterMappings[filterName].name} filter` },
						}}
						value={filters[filterName] || ''}
						onChange={(e) => handleChangeFilter(filterName, e.target.value)}
						data-testid={`${filterName}-entry-field`}
					/>
				);
			case 'boolean':
				return (
					<RadioGroup
						row
						aria-label={`Enter Value for ${MetricsFilterMappings[filterName].name} filter`}
						data-testid={`${filterName}-entry-field`}
						value={filters[filterName]}
						onChange={(e) => handleChangeFilter(filterName, e.target.value)}
						sx={{ justifyContent: 'space-evenly', alignContent: 'centre' }}
					>
						<FormControlLabel value={true} control={<Radio />} label="Yes" />
						<FormControlLabel value={false} control={<Radio />} label="No" />
					</RadioGroup>
				);
			case 'date':
				return (
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
						<DatePicker
							value={dayjs(filters[filterName] as unknown as string | number | undefined)}
							// 2am will never have a DST fault, minimal impact from users not seeing data uploaded at 1am
							onChange={(val) => handleChangeFilter(filterName, dayjs.utc(val).set('hour', 2).valueOf())}
							sx={{ width: '100%' }}
							disableFuture
							//aria-label=`Enter Value for ${MetricsFilterMappings[filterName].name} filter`
							slotProps={{
								textField: {
									inputProps: {
										'aria-label': `Enter Value for ${MetricsFilterMappings[filterName].name} filter`,
										'data-testid': `${filterName}-entry-field`,
									},
								},
							}}
						/>
					</LocalizationProvider>
				);
		}
	};

	useEffect(() => {
		getFilterOptions();
	}, []);

	return (
		<>
			<Stack width="100%" spacing={2} ref={tabHolder}>
				<Typography component="h2" variant="h4">
					Usage Metrics
				</Typography>
				<Typography>
					Use the tool below to generate usage metrics based on data stored when files have been uploaded or processed
					<br />
					By default, all data will be fetched, however you can specify filters to limit results based on the upload/processing time, name
					of the user, case reference, etc.
					<br />
					You can then either preview the data in a table, download without previewing, or preview then download.
				</Typography>

				<FormControl fullWidth variant="outlined">
					<LabelledSelect
						selectLabel="Fetch metrics for..."
						testId="metrics-choice-dropdown"
						value={requestedMetrics}
						onChange={(e) => {
							setRequestedMetrics(e.target.value);
							setPreviewLoaded(false);
							setLoadedData([]);
						}}
					>
						<MenuItem value="upload">Uploads</MenuItem>
						<MenuItem value="operation">Operations (aggregated, top 100)</MenuItem>
					</LabelledSelect>
					<FormHelperText>Preview / Download will not be enabled until this is selected</FormHelperText>
				</FormControl>

				<Button
					fullWidth
					variant="contained"
					endIcon={<ArrowDropDown />}
					onClick={(e) => setFilterAnchor(e.currentTarget)}
					disabled={possibleFilters.length === 0}
					data-testid="add-filter-button"
				>
					{possibleFilters.length > 0 ? 'Add Filter' : 'No filters to add'}
				</Button>
				<Menu
					anchorEl={filterAnchor}
					open={Boolean(filterAnchor)}
					onClose={() => setFilterAnchor(null)}
					disableScrollLock
					slotProps={{ paper: { sx: { minWidth: filterAnchor?.clientWidth } } }}
				>
					{possibleFilters.map((filter) => {
						return (
							<MenuItem key={`filter-select-${filter}`} onClick={() => handleAddFilter(filter)} data-testid={`add-${filter}-filter-button`}>
								{MetricsFilterMappings[filter].name}
							</MenuItem>
						);
					})}
				</Menu>

				{currentFilters.length > 0 && (
					<FormControl fullWidth>
						{currentFilters.map((filter) => {
							const filterName = MetricsFilterMappings[filter].name;
							return (
								<Stack
									direction="row"
									spacing={2}
									justifyContent={'space-evenly'}
									alignContent={'center'}
									key={`metrics-filter-stack-${filter}`}
									paddingY={1}
								>
									<Typography
										alignContent={'center'}
										align="center"
										variant="body1"
										sx={{ flexGrow: 1 }}
										data-testid={`${filter}-filter-description`}
									>
										{filterName}:
									</Typography>
									<Box width={'70%'}>{getFilterComponent(filter)}</Box>
									<Button
										color="error"
										variant="outlined"
										onClick={() => handleRemoveFilter(filter)}
										aria-label={`Delete ${filterName} Filter`}
										data-testid={`delete-${filter}-filter-button`}
									>
										<Delete />
									</Button>
								</Stack>
							);
						})}
					</FormControl>
				)}

				<Stack direction="row" justifyContent={'space-around'} spacing={4}>
					<Button
						fullWidth
						color="info"
						variant="contained"
						startIcon={<Search />}
						disabled={!requestedMetrics || previewLoaded}
						onClick={() => {
							setPreviewLoaded(true);
							setLoadedData([]);
							submitRequest().then((metrics) => setLoadedData(metrics));
						}}
						data-testid="metrics-preview-button"
					>
						Preview
					</Button>
					<Button
						fullWidth
						color="success"
						variant="contained"
						onClick={() => downloadData()}
						startIcon={<Download />}
						disabled={!requestedMetrics || loadingData}
						data-testid="metrics-download-button"
					>
						Download
					</Button>
				</Stack>

				{loadingData && <CircularProgress variant="indeterminate" sx={{ alignSelf: 'center' }} />}

				{loadedData.length > 0 && (
					<Box maxWidth={tabHolder.current?.clientWidth}>
						<MetricsTable metricsData={loadedData} tableType={requestedMetrics.toUpperCase() as 'UPLOAD' | 'OPERATION'} />
					</Box>
				)}
			</Stack>

			{loadedData.length > 0 && showFab && (
				<Tooltip title="Scroll to top">
					<Fab
						color="primary"
						onClick={() => {
							window.scrollTo(0, 0);
							setShowFab(false);
						}}
						sx={{ zIndex: 99999, position: 'sticky', bottom: 24 }}
						data-testid="metrics-scroll-to-top-button"
					>
						<KeyboardDoubleArrowUp />
					</Fab>
				</Tooltip>
			)}

			<SnackbarAlert
				severity="error"
				open={Boolean(loadError)}
				onClose={() => setLoadError('')}
				alertMessage={loadError}
				testId="load-error-snackbar"
			/>
		</>
	);
};

export default MetricsViewTab;
